/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: auth.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Authentication store for managing user authentication state,
    login/logout functionality, and user session management.

 🔧 Notes:
    - Implements Zustand store with persistence middleware
    - Manages user authentication state and session data
    - Handles login, logout, and token management
    - Supports multi-tenant user authentication
    - Integrates with API client for authentication requests
    - Provides user profile and permissions management

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { api } from '../lib/api';
import { useThemeStore } from './theme';
import { useDebugModeStore } from './debugMode';

interface User {
  id: string;
  email: string;
  username?: string;
  fullName?: string;
  department?: string;
  avatar?: string;
  locale?: string;
  timezone?: string;
  themePrefs?: Record<string, unknown>;
  isActive?: boolean;
  emailVerified?: boolean;
  lastLoginAt?: Date;
  startupDialog?: string;
  startModuleUrl?: string; // 🆕 URL of the module to load on login
  tenant?: {
    id: string;
    name: string;
    slug: string;
    description?: string;
    logo?: string;
    favicon?: string;
    primaryColor?: string;
    secondaryColor?: string;
    defaultLocale?: string;
    timezone?: string;
  };
  tenants?: Array<{
    id: string;
    name: string;
    slug: string;
    role?: {
      id: string;
      name: string;
    };
    logProfile?: {
      id: string;
      name: string;
    };
  }>;
  roles: Array<{
    id: string;
    name: string;
    permissions: Array<{
      moduleId: string;
      moduleName: string;
      level: string;
    }>;
  }>;
  debugMode?: boolean; // Permission to enable debug mode
  debugModeActive?: boolean; // Current debug mode state
  isPrimaryAdmin?: boolean; // System-wide super admin bypass flag
  extensionNumber?: string; // User's assigned extension for WebRTC phone
  softphoneEnabled?: boolean; // Whether softphone is enabled for this user
  chatbotEnabled?: boolean; // Whether chatbot is enabled for this user
  chatbotAgentId?: number | null; // Associated AI agent ID for chatbot
}

interface AuthState {
  isAuthenticated: boolean;
  isInitializing: boolean;  // true while session is being verified on page load
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  localLogout: () => void;   // Sync: clears local state immediately (no API call)
  refreshAccessToken: () => Promise<void>;
  initializeAuth: () => Promise<void>;  // Now async — verifies session with backend
  clearError: () => void;
  updateUser: (userData: Partial<User>) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      isAuthenticated: false,
      isInitializing: true,   // blocks render of protected routes until session verified
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      error: null,

      // Actions
      login: async (identifier: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/api/auth/login', {
            identifier,
            password
          });

          const { user, expiresAt } = response.data;

          // Set tenant header for future requests
          api.defaults.headers['X-Tenant-Slug'] = user.tenant.slug;
          // Authorization header is no longer needed with cookies, but we clear it just in case
          delete api.defaults.headers.Authorization;

          // Store tenant slug in localStorage for interceptor
          localStorage.setItem('tenant-slug', user.tenant.slug);
          // Remove potential legacy tokens
          localStorage.removeItem('access-token');
          localStorage.removeItem('refresh-token');

          set({
            isAuthenticated: true,
            user,
            accessToken: 'cookie-handled',
            refreshToken: 'cookie-handled',
            isLoading: false,
            error: null
          });

          // Notify SessionWarningModal: expiresAt comes from the server's JWT exp claim
          // (always in sync with JWT_ACCESS_EXPIRES_IN in .env — never hardcoded client-side)
          window.dispatchEvent(new CustomEvent('session:issued', { detail: { expiresAt } }));

          // Initialize debug mode from user data
          useDebugModeStore.getState().initializeFromUser(user);

          // Load user's theme preferences from database after successful login
          try {
            const themeStore = useThemeStore.getState();
            await themeStore.loadThemePrefs();
          } catch (themeError) {
            // Don't fail login if theme loading fails
          }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          set({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            isLoading: false,
            error: error.response?.data?.message || 'Login failed'
          });
          throw error;
        }
      },

      /**
       * localLogout — synchronous, no API call.
       * Use this when the session is already dead server-side (e.g. 401 after
       * failed refresh). Clears local state immediately so the app re-renders
       * to the login shell without waiting for any async operation.
       */
      localLogout: () => {
        // Clear API headers
        delete api.defaults.headers['X-Tenant-Slug'];
        delete api.defaults.headers.Authorization;

        // Clear localStorage
        localStorage.removeItem('access-token');
        localStorage.removeItem('refresh-token');
        localStorage.removeItem('tenant-slug');
        localStorage.removeItem('auth-storage');

        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          isLoading: false,
          error: null
        });
      },

      /**
       * logout — tells the backend to invalidate the session, then clears
       * local state. Use this for deliberate user-initiated logouts.
       */
      logout: async () => {
        // Call backend logout endpoint to register the event (best effort)
        try {
          await api.post('/api/auth/logout');
        } catch (error) {
          // Continue with logout even if backend call fails
          console.warn('Logout API call failed, continuing with local logout:', error);
        }

        // Reuse the sync cleanup
        get().localLogout();
      },

      refreshAccessToken: async () => {
        // With cookies, the refresh token is sent automatically in the cookie
        // We just need to trigger the refresh endpoint
        try {
          // No body needed as token is in cookie
          await api.post('/api/auth/refresh');

          // No need to set accessToken in state/headers as it's in cookie now
          // We just acknowledge success
          // Ideally we might want to check if new access token came back in body?
          // If backend sets new access cookie, we are good.
        } catch (error) {
          // Refresh failed, logout user
          get().logout();
          throw error;
        }
      },

      /**
       * initializeAuth — async.
       * Called once on app mount. Verifies the session with the backend
       * before rendering any protected route. If /api/auth/me returns 401
       * the persisted state is cleared immediately and the login page is shown.
       */
      initializeAuth: async () => {
        const state = get();

        // If there is no persisted user at all, skip the network call
        if (!state.user) {
          set({ isInitializing: false, isAuthenticated: false });
          return;
        }

        // Set the tenant header so the verification request is routed correctly
        if (state.user.tenant?.slug) {
          api.defaults.headers['X-Tenant-Slug'] = state.user.tenant.slug;
          localStorage.setItem('tenant-slug', state.user.tenant.slug);
        }

        try {
          // Verify the session cookie is still valid with an ultra-lightweight endpoint
          const meResponse = await axios.get(`${api.defaults.baseURL || ''}/api/auth/me`, {
            withCredentials: true,
            headers: { 'X-Tenant-Slug': state.user.tenant?.slug || 'main' },
            validateStatus: (status) => status === 200 || status === 401
          });

          if (meResponse.status === 401) {
            get().localLogout();
            set({ isInitializing: false });
            return;
          }

          // Cookie is valid — restore auth state
          set({ isAuthenticated: true, isInitializing: false });
          useDebugModeStore.getState().initializeFromUser(state.user);

          // Use expiresAt from the server (derived from the JWT exp claim)
          // This is always in sync with JWT_ACCESS_EXPIRES_IN in .env
          const expiresAt: number | undefined = meResponse.data?.expiresAt;
          if (expiresAt) {
            window.dispatchEvent(new CustomEvent('session:issued', { detail: { expiresAt } }));
          }

        } catch {
          // Session is dead — purge everything and show the login page
          get().localLogout();
          set({ isInitializing: false });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } });
        }
      }
    }),
    {
      name: 'auth-storage',
      // Persist only fields that survive a page reload.
      // isInitializing is intentionally EXCLUDED so it always resets to true
      // on mount, forcing a fresh session check every time the page loads.
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken
      })
    }
  )
);
